import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Table, Card, Typography, Button, message, Row, Col, Statistic, DatePicker } from 'antd';
import { ArrowLeftOutlined, PrinterOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { apiClient } from '../Api/AllApi';
import { formatCurrency } from '../../utils/formatters';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const { Title } = Typography;

const CustomerOrderHistory = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [orders, setOrders] = useState([]);
  const [customerInfo, setCustomerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([null, null]);
  const [totalWeight, settotalWeightOrders] = useState(0);

  useEffect(() => {
    fetchCustomerOrders();
  }, [id]);

  const handleDateChange = async (dates) => {
    setDateRange(dates);

    if (dates && dates.length === 2) {
      const start_date = dayjs(dates[0]).format('YYYY-MM-DD');
      const end_date = dayjs(dates[1]).format('YYYY-MM-DD');

      try {
        await fetchCustomerOrders(start_date, end_date);
      } catch (error) {
        console.error('Error fetching filtered orders:', error);
      }
    }
  };

  const fetchCustomerOrders = async (start_date, end_date) => {
    try {
      const response = await apiClient.get(`/customers/${id}/orders`, {
        params: { start_date, end_date },
      });
      setOrders(response.data.orders);
      settotalWeightOrders(response.data.total_weight || 0);
      setCustomerInfo(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching orders:', error);
      message.error(t('fetchError'));
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      render: (_, __, index) => index + 1,
      width: 70,
    },
    {
      title: <b>{t('ژمارەی داواکاری')}</b>,
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: <b>{t('بەرواری داواکاری')}</b>,
      dataIndex: 'created_at',
      key: 'created_at',
      render: function(text) {
        if (!text) return '';
        return dayjs(text).utc().format('DD/MM/YYYY HH:mm:ss'); // Format as DD/MM/YYYY HH:mm:ss in UTC
      },
    },
    {
      title: <b>{t('کێشی داواکاری')}</b>,
      dataIndex: 'total_order_weight',
      key: 'total_order_weight',
       render: (text) => {
        const raw = parseFloat((text || '0').toString().replace(/,/g, ''));
        return `${raw.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })} kg`;
      },
    },
    {
      title: <b>{t('کۆی گشتی بڕی')}</b>,
      dataIndex: 'total_price',
      key: 'total_price',
      render: (text) => formatCurrency(text, 'IQD'),
    },
    {
      title: <b>{t('بڕی پارە دراو')}</b>,
      dataIndex: 'amount_paid',
      key: 'amount_paid',
      render: (text) => formatCurrency(text, 'IQD'),
    },
    {
      title: <b>{t('باڵانسی نەدراو')}</b>,
      dataIndex: 'outstanding_balance',
      key: 'outstanding_balance',
      render: (text) => formatCurrency(text, 'IQD'),
    },
    {
      title: <b>{t('کردارەکان')}</b>,
      key: 'actions',
      render: (_, record) => (
        <>
          <Link to={`/customer/${id}/order/${record.id}`} style={{ marginLeft: 8 }}>
            <Button type="primary">{t('view')}</Button>
          </Link>
          <Link to={`/TableOrder/InfoOrder/${record.id}`} target="_blank">
            <Button icon={<PrinterOutlined />} style={{ background: '#faad14', color: '#fff', border: 'none', marginRight: 0 }} />
          </Link>
        </>
      ),
    },
  ];

  // Defensive: always treat dateRange as array, fallback to [null, null] if null
  const safeDateRange = Array.isArray(dateRange) ? dateRange : [null, null];

  const filteredOrders = orders.filter(order => {
    if (!safeDateRange[0] || !safeDateRange[1]) return true;
    const orderDate = dayjs(order.created_at);
    return orderDate.isAfter(safeDateRange[0].startOf('day').subtract(1, 'ms')) && orderDate.isBefore(safeDateRange[1].endOf('day').add(1, 'ms'));
  });

  // Use filteredOrders for statistics if a date range is selected
  const statsOrders = (safeDateRange[0] && safeDateRange[1]) ? filteredOrders : orders;
  const statsTotalPrice = statsOrders.reduce((sum, order) => sum + parseFloat(order.total_price || 0), 0);
  const statsTotalPaid = parseFloat(String(customerInfo?.total_paid ?? '').replace(/,/g, '')) || 0;
  const statsTotalOutstanding = parseFloat(String(customerInfo?.total_outstanding ?? '').replace(/,/g, '')) || 0;
  const statsTotalWeight = totalWeight || 0;

  if (!id) {
    return (
      <div style={{ textAlign: 'center', marginTop: '50px' }}>
        <h1>Invalid or Missing Customer ID</h1>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px', direction: 'rtl' }}>
      <Card
        style={{
          marginBottom: '20px',
          background: '#f0f2f5',
          borderRadius: '15px',
          padding: '20px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Title level={3} style={{ margin: 0 }}>
            {customerInfo ? `${customerInfo.name}` : t('orderHistory')}
          </Title>
          <Link to="/GetCustomer">
            <Button type="primary" icon={<ArrowLeftOutlined />}>
              {t('back')}
            </Button>
          </Link>
        </div>
      </Card>

      <Row gutter={[24, 24]} style={{ marginBottom: '20px' }}>
        <Col xs={24} sm={12} md={12} lg={4}>
          <Card style={{ background: '#f6ffed', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('کۆی گشتی داواکارییەکان')}
              value={statsOrders.length}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={5}>
          <Card style={{ background: '#e6f7ff', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('کۆی گشتی بڕی')}
              value={formatCurrency(statsTotalPrice, 'IQD')}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={5}>
          <Card style={{ background: '#d9f7be', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('کۆی گشتی پارەی پێدراوە')}
              value={formatCurrency(statsTotalPaid, 'IQD')}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={5}>
          <Card style={{ background: '#fff1f0', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('کۆی گشتی قەرزی نەدراو')}
              value={formatCurrency(statsTotalOutstanding, 'IQD')}
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} md={12} lg={5}>
          <Card style={{ background: '#fff7e6', borderRadius: '10px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Statistic
              title={t('کۆی گشتی کێش')}
              value={statsTotalWeight}
              suffix="kg"
              style={{ textAlign: 'center' }}
            />
          </Card>
        </Col>
      </Row>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
        <div style={{ direction: 'ltr', width: 'fit-content' }}>
          <DatePicker.RangePicker
            value={safeDateRange}
            onChange={handleDateChange}
            format="YYYY-MM-DD"
            style={{ minWidth: 260 }}
            allowClear
            placeholder={[t('start Date'), t('end Date')]}
            inputReadOnly
          />
        </div>
      </div>
      <Card
        style={{
          background: '#ffffff',
          borderRadius: '15px',
          padding: '20px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        }}
      >
        <Table
          dataSource={orders}
          columns={columns}
          pagination={{ pageSize: 50 }}
          loading={loading}
          rowKey={(record) => record.id}
        />
      </Card>
    </div>
  );
};

export default CustomerOrderHistory;
